// @flow
import React, { Fragment, useMemo } from 'react';
import ErrorBoundary from 'app/packages/error-boundary';
import SaveRoutePins from 'app/common/react/components/Masonry/SaveRoutePins';
import useGridBottomSpinnerProfiler from 'app/common/react/components/Masonry/useGridBottomSpinnerProfiler';
import { useExperiment } from 'app/common/react/components/experiments/useExperiment';
import ExperimentalMasonry, {
  type MasonryPaddingStyle,
  type MasonryType,
} from 'app/common/react/ui/experimental/ExperimentalMasonry';
import MasonryVisuallyCompleteProfiler, {
  type ProfilerInput,
} from 'app/common/react/components/Masonry/MasonryVisuallyCompleteProfiler';

type ExperimentalMasonryProps = {|
  columnWidth?: number,
  comp: Function,
  fetchBoundsOffset?: number,
  flexible?: boolean,
  gutterWidth?: number,
  isGridCentered?: boolean,
  items: Array<Object>,
  loadItems: () => void,
  masonryRef?: { current: ?MasonryType },
  minCols?: number,
  paddingStyle?: MasonryPaddingStyle,
  scrollContainer?: () => HTMLElement,
  serverRender?: boolean,
  useMasonry9?: boolean,
  virtualize?: boolean,
  widthOffset?: number,
|};

export type Props = {|
  error?: Error,
  saveRoutePins?: boolean,
  viewParameterType: any,
  ...ExperimentalMasonryProps,
  ...ProfilerInput,
|};

const getWindow = typeof window !== 'undefined' ? () => window : undefined;

export default function MasonryPerfTiming({
  cacheKey,
  comp: GridItem,
  error,
  fetchBoundsOffset,
  isFetching,
  isGridCentered = true,
  items,
  loadItems: loadItemsProp,
  mapPinUrlsToItems,
  paddingStyle,
  saveRoutePins,
  scrollContainer,
  serverRender = true,
  useMasonry9: useMasonry9Prop,
  viewParameterType,
  ...rest
}: Props) {
  const eligibleForMasonry9Exp = useMasonry9Prop === undefined;
  const { anyEnabled: masonry9ExpEnabled } = useExperiment('web_masonry9', eligibleForMasonry9Exp);
  const useMasonry9 = eligibleForMasonry9Exp ? masonry9ExpEnabled : !!useMasonry9Prop;

  const GridItemWithErrorBoundary = useMemo(
    () => (props) => (
      <ErrorBoundary name="MasonryPerfTimingItem">
        <GridItem {...props} />
      </ErrorBoundary>
    ),
    [GridItem],
  );

  const startGridBottomSpinnerTiming = useGridBottomSpinnerProfiler({
    isFetching,
    viewParameterType,
  });

  const loadItems = () => {
    loadItemsProp();
    startGridBottomSpinnerTiming();
  };

  return (
    <Fragment>
      {!!saveRoutePins && <SaveRoutePins items={items} />}
      <MasonryVisuallyCompleteProfiler
        cacheKey={cacheKey}
        isFetching={isFetching}
        items={items}
        mapPinUrlsToItems={mapPinUrlsToItems}
        useMasonry9={useMasonry9}
      >
        {(measurementStore) => (
          <ExperimentalMasonry
            {...rest}
            comp={GridItemWithErrorBoundary}
            fetchBoundsOffset={fetchBoundsOffset || (useMasonry9 ? 100 : 0)}
            isGridCentered={isGridCentered}
            items={items}
            loadItems={loadItems}
            measurementStore={measurementStore}
            paddingStyle={paddingStyle}
            scrollContainer={scrollContainer || getWindow}
            serverRender={serverRender}
            useMasonry9={useMasonry9}
          />
        )}
      </MasonryVisuallyCompleteProfiler>
    </Fragment>
  );
}
